import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Pencil, Pill, Plus } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@roger/ui-components';
import { useToast } from '../../hooks/useToastContext';
import { getPatientPharmacy, upsertPatientPharmacy } from '../../services/api';
import type { PatientPharmacyResponse } from '../../services/api';

interface PharmacyCardProps {
  patientId: string;
}

interface PharmacyFormState {
  name: string;
  address_one: string;
  address_two: string;
  city: string;
  state: string;
  zip_code: string;
  phone: string;
}

/** Matches the compact field labels used elsewhere (e.g. the task list rows). */
const PHARMACY_LABEL_CLASS =
  'flex-none text-[11px] font-medium uppercase tracking-wide text-muted-foreground';

const EMPTY_FORM: PharmacyFormState = {
  name: '',
  address_one: '',
  address_two: '',
  city: '',
  state: '',
  zip_code: '',
  phone: '',
};

function pharmacyToForm(
  pharmacy: PatientPharmacyResponse | null
): PharmacyFormState {
  if (!pharmacy) return { ...EMPTY_FORM };
  return {
    name: pharmacy.name ?? '',
    address_one: pharmacy.address_one ?? '',
    address_two: pharmacy.address_two ?? '',
    city: pharmacy.city ?? '',
    state: pharmacy.state ?? '',
    zip_code: pharmacy.zip_code ?? '',
    phone: pharmacy.phone ?? '',
  };
}

function hasAnyValue(pharmacy: PatientPharmacyResponse | null): boolean {
  if (!pharmacy) return false;
  return Boolean(
    pharmacy.name ||
      pharmacy.address_one ||
      pharmacy.address_two ||
      pharmacy.city ||
      pharmacy.state ||
      pharmacy.zip_code ||
      pharmacy.phone
  );
}

function formatAddress(pharmacy: PatientPharmacyResponse): string | null {
  const parts: string[] = [];
  if (pharmacy.address_one) parts.push(pharmacy.address_one);
  if (pharmacy.address_two) parts.push(pharmacy.address_two);

  const cityStateZip: string[] = [];
  if (pharmacy.city) cityStateZip.push(pharmacy.city);
  if (pharmacy.state) {
    if (cityStateZip.length > 0) {
      cityStateZip[cityStateZip.length - 1] += ',';
    }
    cityStateZip.push(pharmacy.state);
  }
  if (pharmacy.zip_code) cityStateZip.push(pharmacy.zip_code);

  if (cityStateZip.length > 0) parts.push(cityStateZip.join(' '));

  return parts.length > 0 ? parts.join(', ') : null;
}

const PharmacyCard: React.FC<PharmacyCardProps> = ({ patientId }) => {
  const [pharmacy, setPharmacy] = useState<PatientPharmacyResponse | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState<PharmacyFormState>({ ...EMPTY_FORM });
  const { error: showError } = useToast();

  const fetchPharmacy = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getPatientPharmacy({
        path: { patient_id: patientId },
      });
      // The client is configured without `throwOnError`, so HTTP failures come
      // back as a populated `error` rather than a thrown exception. Surface the
      // failure instead of falling through to the empty state, which would
      // wrongly imply no pharmacy is on file.
      if (response.error) {
        console.error('Failed to fetch pharmacy:', response.error);
        setLoadFailed(true);
        showError('Failed to load pharmacy information. Please try again.');
        return;
      }
      setPharmacy(response.data ?? null);
      setLoadFailed(false);
    } catch (err) {
      console.error('Failed to fetch pharmacy:', err);
      setLoadFailed(true);
      showError('Failed to load pharmacy information. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [patientId, showError]);

  useEffect(() => {
    fetchPharmacy();
  }, [fetchPharmacy]);

  const openDialog = () => {
    setForm(pharmacyToForm(pharmacy));
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await upsertPatientPharmacy({
        path: { patient_id: patientId },
        body: {
          name: form.name || null,
          address_one: form.address_one || null,
          address_two: form.address_two || null,
          city: form.city || null,
          state: form.state || null,
          zip_code: form.zip_code || null,
          phone: form.phone || null,
        },
      });
      // HTTP failures come back as a populated `error`, not a thrown
      // exception, so keep the dialog open and report instead of closing it
      // as though the save succeeded.
      if (response.error) {
        console.error('Failed to save pharmacy:', response.error);
        showError('Failed to save pharmacy. Please try again.');
        return;
      }
      // Saving with every field blank clears the pharmacy, and the backend
      // answers with null — reflect that instead of keeping the stale record.
      setPharmacy(response.data ?? null);
      setDialogOpen(false);
    } catch (err) {
      console.error('Failed to save pharmacy:', err);
      showError('Failed to save pharmacy. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = (field: keyof PharmacyFormState, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-6'>
        <h4 className='sr-only'>Pharmacy</h4>
        <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
      </div>
    );
  }

  if (loadFailed) {
    return (
      <div className='mb-6'>
        <div className='flex items-center gap-3 rounded-md border bg-muted/40 px-4 py-2'>
          <span className='flex h-7 w-7 flex-none items-center justify-center rounded-full bg-primary/10 text-primary'>
            <Pill className='h-4 w-4' />
          </span>
          <h4 className={PHARMACY_LABEL_CLASS}>Pharmacy</h4>
          <span className='min-w-0 flex-1 text-sm text-muted-foreground'>
            Unable to load.
          </span>
          <Button
            onClick={fetchPharmacy}
            variant='outline'
            size='sm'
            className='flex-none'
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const showContent = hasAnyValue(pharmacy);
  const address = pharmacy ? formatAddress(pharmacy) : null;
  // Only the parts on file, so a missing name leaves no placeholder or dangling
  // separator behind — the row's label already identifies it as the pharmacy.
  const summaryParts = pharmacy
    ? [pharmacy.name, address, pharmacy.phone].filter((part): part is string =>
        Boolean(part)
      )
    : [];

  return (
    <>
      <div className='mb-6'>
        <div className='flex items-center gap-3 rounded-md border bg-muted/40 px-4 py-2'>
          <span className='flex h-7 w-7 flex-none items-center justify-center rounded-full bg-primary/10 text-primary'>
            <Pill className='h-4 w-4' />
          </span>
          {/* Names alone don't always read as a pharmacy ("Smith & Sons"), and
              the pill icon is ambiguous, so the row is labelled outright. */}
          <h4 className={PHARMACY_LABEL_CLASS}>Pharmacy</h4>
          {showContent && pharmacy ? (
            <div
              className='min-w-0 flex-1 truncate text-sm text-foreground'
              title={summaryParts.join(' | ')}
            >
              {summaryParts.map((part, index) => (
                <React.Fragment key={index}>
                  {index > 0 && (
                    <span className='mx-2 text-muted-foreground'>|</span>
                  )}
                  <span
                    className={
                      part === pharmacy.name ? 'font-medium' : undefined
                    }
                  >
                    {part}
                  </span>
                </React.Fragment>
              ))}
            </div>
          ) : (
            <span className='min-w-0 flex-1 text-sm text-muted-foreground'>
              None on file.
            </span>
          )}
          <Button
            onClick={openDialog}
            variant='outline'
            size='sm'
            className='flex-none'
          >
            {showContent ? (
              <>
                <Pencil className='h-3.5 w-3.5 mr-1.5' />
                Edit
              </>
            ) : (
              <>
                <Plus className='h-3.5 w-3.5 mr-1.5' />
                Add
              </>
            )}
          </Button>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className='sm:max-w-md'>
          <DialogHeader>
            <DialogTitle>
              {showContent ? 'Edit Pharmacy' : 'Add Pharmacy'}
            </DialogTitle>
            <DialogDescription>
              The pharmacy this patient uses. Clearing every field removes it.
            </DialogDescription>
          </DialogHeader>
          <div className='grid gap-4 py-4'>
            <div className='grid gap-2'>
              <Label htmlFor='pharmacy-name'>Pharmacy Name</Label>
              <Input
                id='pharmacy-name'
                value={form.name}
                onChange={e => updateField('name', e.target.value)}
                inputSize='sm'
              />
            </div>
            <div className='grid gap-2'>
              <Label htmlFor='pharmacy-address-one'>Address</Label>
              <Input
                id='pharmacy-address-one'
                value={form.address_one}
                onChange={e => updateField('address_one', e.target.value)}
                inputSize='sm'
              />
            </div>
            <div className='grid gap-2'>
              <Label htmlFor='pharmacy-address-two'>Address Line 2</Label>
              <Input
                id='pharmacy-address-two'
                value={form.address_two}
                onChange={e => updateField('address_two', e.target.value)}
                inputSize='sm'
              />
            </div>
            <div className='grid grid-cols-3 gap-3'>
              <div className='grid gap-2'>
                <Label htmlFor='pharmacy-city'>City</Label>
                <Input
                  id='pharmacy-city'
                  value={form.city}
                  onChange={e => updateField('city', e.target.value)}
                  inputSize='sm'
                />
              </div>
              <div className='grid gap-2'>
                <Label htmlFor='pharmacy-state'>State</Label>
                <Input
                  id='pharmacy-state'
                  value={form.state}
                  onChange={e => updateField('state', e.target.value)}
                  maxLength={2}
                  inputSize='sm'
                />
              </div>
              <div className='grid gap-2'>
                <Label htmlFor='pharmacy-zip'>Zip</Label>
                <Input
                  id='pharmacy-zip'
                  value={form.zip_code}
                  onChange={e => updateField('zip_code', e.target.value)}
                  inputSize='sm'
                />
              </div>
            </div>
            <div className='grid gap-2'>
              <Label htmlFor='pharmacy-phone'>Phone</Label>
              <Input
                id='pharmacy-phone'
                value={form.phone}
                onChange={e => updateField('phone', e.target.value)}
                inputSize='sm'
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setDialogOpen(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving && <Loader2 className='h-4 w-4 mr-1.5 animate-spin' />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PharmacyCard;
